"use client";

import { useEffect, useRef } from "react";
import { useSnapshot } from "valtio";

import { usePathname } from "@/src/hooks/usePathname";
import { isInterruptiveSurfaceRoute } from "@/src/hooks/onboarding/isOnboardingRoute";
import { useGlobalAppModalLimit } from "@/src/hooks/useGlobalAppModalLimit";
import { useShellRouter } from "@hooks/useShellAwareRouter";
import { handoffSessionStore } from "@hooks/onboarding/handoff/handoffSessionStore";
import { normalizePathname } from "@utils/routeNormalizer";
import { useAppFeatures, useBridge } from "@/src/lib/bridge";
import { Track } from "@/src/lib/tracking";
import { buildBriefReportHref } from "@screens/report/buildReportSequenceItems";
import { useReportSummary } from "@screens/brief/hooks/useReportSummary";
import { rootStore } from "@store/rootStore";
import { ModalStore } from "@store/modules/modal";
import { ROUTER_PATH } from "@config/router";
import {
  hasReportOpeningEntryToday,
  markReportOpeningEntered,
} from "./reportOpeningAutoshowStorage";

/** useGlobalAppModalLimit 弹窗标识（APP 多 WebView 协调备用；本期 APP 未接入自动弹） */
const REPORT_OPENING_AUTOSHOW_ID = "report-opening";

/**
 * 远程可配开关占位（fe-spec §3.8.1/§3.2）：本期默认开；
 * 远程配置接入后替换为远程读取（GAP：配置通道未定）。
 */
const REPORT_OPENING_AUTOSHOW_REMOTE_ENABLED = true;

/**
 * 迁移自旧 catchup 自动弹 hook 的 isPeerAutoModalOpen（fe-spec §3.8.1：
 * 旧 tracker welcome 弹窗态已随 catchup 下线、low credit 弹窗已下线，
 * 四弹窗互斥降为三弹窗内部互斥——
 * 新自动弹是路由 push 非弹窗，无新 modal-visible 态可读）。
 */
function isPeerAutoModalOpen(): boolean {
  // EDG-479/481：`turnOnNotificationModalVisible` 已随 web 版「开启通知」弹窗一起删除
  //（App 内 push 引导归原生，见 `features.nativePushPrimer`）⇒ 三弹窗互斥降为两弹窗。
  return !!(
    ModalStore.appVersionUpdateModalVisible || ModalStore.appRatingModalVisible
  );
}

function isReportRoute(pathname: string): boolean {
  return (
    pathname === ROUTER_PATH.BRIEF_REPORT ||
    pathname.startsWith(`${ROUTER_PATH.BRIEF_REPORT}/`)
  );
}

/**
 * 开场页自动弹（fe-spec §3.8.1/§3.2 · PRD-A S1.1）：当日首次打开 App 且
 * `unread_count >= 1` 时 `router.push('/brief/report?entry=auto')`。
 *
 * - 归属：App 内整条不参与（`features.nativeReportOpening`，原生唯一承载）
 * - 门槛：登录 + useReportSummary 的 unread_count ≥ 1（S2 query，取数失败/超时按 0 不弹）
 * - 额度：per owner × 用户本地自然日至多 1 次；同一自然日已被自动弹过或已主动进过
 *   只能从摘要条进入（PRD S1.1）——自动弹 push 与落到 /brief/report 均落定当日额度
 * - 互斥：peer 自动弹窗（appVersionUpdate / appRating）
 *   任一显示中不弹；关闭后随 effect 重试（attemptedRef 只在真正尝试后置位）
 * - 互斥：Handoff tour（onboarding 完成后的全屏手势引导，挂应用根部与路由无关）
 *   进行中让路——不 push、不置 attemptedRef、不消耗额度；引导结束后随
 *   handoffSession.active 翻转自动重试（两个「首次引导」互斥，防 coachmark
 *   全屏滞留叠在开场页上，验收实锤 2026-09）
 * - 远程开关：占位默认开（REPORT_OPENING_AUTOSHOW_REMOTE_ENABLED）
 * - fail-safe：额度写入失败不弹（Track.error；宁可少弹不可双弹，fe-spec §2.4）
 *
 * App 多 WebView：useGlobalAppModalLimit 协调 visible / broadcast / 远程关弹（机制迁移自
 * 旧 catchup 自动弹 hook，本期 APP 未接入自动弹，保留备用）。Web/H5 单页不校验
 * visible、claim 前即标记 attempted。
 */
export function useReportOpeningAutoshow() {
  const router = useShellRouter();
  const { isInApp } = useBridge();
  const pathname = usePathname();
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const { ownerInfo } = useSnapshot(rootStore.user);
  const handoffSession = useSnapshot(handoffSessionStore);
  const ownerId = ownerInfo?.id;
  /**
   * App 内开场页自动弹归原生（kmp `ReportOpeningAutoshowPrompt`）⇒ 本 hook 整条不参与。
   * 与 `nativePushPrimer` / `nativePaywall` 同一手法：能力**所有权**只归一端。
   *
   * 时序：flag 随 bridge `app-init` 到达，可能晚于首帧几十毫秒。这里不额外等
   * —— 弹出前置条件是 `unreadCount >= 1`，它要一次全量翻页（数百毫秒起），
   * 远晚于 app-init；真的抢跑也只是白发一次请求，不会误弹。
   */
  const nativeOwnsReportOpening = useAppFeatures().nativeReportOpening === true;
  const { unreadCount } = useReportSummary({
    enabled: !nativeOwnsReportOpening,
  });
  const { appVersionUpdateModalVisible, appRatingModalVisible } =
    useSnapshot(ModalStore);
  const attemptedRef = useRef(false);
  const { canShow, isSuppressed, tryOpen } = useGlobalAppModalLimit(
    REPORT_OPENING_AUTOSHOW_ID,
    { hideInAppChatModal: true },
  );
  const tryOpenRef = useRef(tryOpen);
  tryOpenRef.current = tryOpen;

  // 「已主动进过」也消耗当日额度（PRD S1.1）：落到 /brief/report 即落定
  // （auto push 后的路由变化同样经此幂等写；写入失败静默——下次进入重试落定）
  useEffect(() => {
    if (!ownerId || !isReportRoute(pathname)) {
      return;
    }
    markReportOpeningEntered(ownerId);
  }, [ownerId, pathname]);

  useEffect(() => {
    if (!REPORT_OPENING_AUTOSHOW_REMOTE_ENABLED) {
      return;
    }
    // App 内归原生（见 `nativeOwnsReportOpening` 注释）——不置 attemptedRef，纯粹不参与
    if (nativeOwnsReportOpening) {
      return;
    }
    if (!isLoggedIn || unreadCount < 1) {
      return;
    }
    if (!ownerId) {
      return;
    }
    // 仅在 Brief 主壳上评估/弹出（与 kmp onBriefShell 门控同口径）：取数窗口内用户点
    // brief 卡片进详情等路由 → pathname 变化 effect 重跑=挂起等待，回主壳后自动重试——
    // 防开场页顶掉详情路由（验收实锤 2026-09-08：先显示详情、接着开场页弹出）
    if (pathname !== ROUTER_PATH.BRIEF) {
      return;
    }
    if (isInterruptiveSurfaceRoute(pathname)) {
      return;
    }
    if (attemptedRef.current) {
      return;
    }
    // Handoff tour 进行中让路：不 push、不置 attemptedRef、不消耗额度——
    // 引导结束后 handoffSession.active 翻转，本 effect 自动重试
    if (handoffSession.active) {
      return;
    }
    if (isPeerAutoModalOpen()) {
      return;
    }
    // 当日已弹过 / 已主动进过 → 不再自动弹（AC23；之后只能从摘要条进入）
    if (hasReportOpeningEntryToday(ownerId)) {
      attemptedRef.current = true;
      return;
    }

    const pushOpening = () => {
      // 终检：取数窗口内用户可能已离开 Brief 主壳（点 brief 卡片进详情等路由）——
      // 此时弹开场页 = 打扰并顶掉详情（详情路由会被开场页替换），放弃本次：
      // 不写额度（fail-safe 同语义），attempted 已置位本会话不再评估，下次冷启/登录重试。
      //
      // ① **`normalizePathname` 不可省**：`window.location.pathname` 是浏览器原始路径、带
      //    locale 前缀。非英文 locale 由 proxy 走 redirect（`MobileRouting.buildResponse`：
      //    `shouldUseEnglishPath` 只对 `en` 为真）⇒ zh-tw 用户地址栏恒为 `/zh-tw/brief`，
      //    裸比 `ROUTER_PATH.BRIEF` 永不相等 ⇒ 自动弹对全部 zh-tw 用户**静默失效**
      //    （attempted 已置位、额度未写、无任何日志）。code review P0 2026-09-08。
      //    全仓拿 `window.location.pathname` 比路由的地方都套了它
      //    （`lib/bridge/shellAppNavigation.ts:154/599/612`），这里不是例外。
      //
      // ② 这道门当前是**冗余的防御**，不是在挡一个活着的 bug —— 别按「旧 async 续体的唯一闸门」
      //    理解（那个说法不成立）：本 effect 体从头到尾同步，`tryOpen` 也是同步调
      //    （`useGlobalAppModalLimit` 里直接 `open()`，无 await/定时器），`!isInApp` 分支
      //    更是直接 `pushOpening()`；而 `pathname` 就在本 effect 的 deps 里，路由一变 effect 重跑。
      //    ⇒ 本函数与上面那道 `pathname !== BRIEF` 卫语句跑在**同一 tick**，闭包不可能过期。
      //    留着它的理由只有一条：万一以后改成 `scheduleOpen`（延迟打开）就真需要它。
      //    **改成异步展示的人请回来看这条**。
      if (normalizePathname(window.location.pathname) !== ROUTER_PATH.BRIEF) {
        return;
      }
      // fail-safe：额度写入失败不弹（宁可少弹不可双弹）
      if (!markReportOpeningEntered(ownerId)) {
        Track.error(new Error("report opening autoshow quota write failed"), {
          tag: "useReportOpeningAutoshow",
          msg: "quota storage write failed, skip autoshow",
        });
        return;
      }
      router.push(buildBriefReportHref({ entry: "auto" }));
    };

    if (!isInApp) {
      attemptedRef.current = true;
      pushOpening();
      return;
    }

    if (isSuppressed) {
      attemptedRef.current = true;
      return;
    }
    if (!canShow) {
      return;
    }

    attemptedRef.current = true;
    tryOpenRef.current(pushOpening);
  }, [
    isLoggedIn,
    nativeOwnsReportOpening,
    ownerId,
    unreadCount,
    pathname,
    isInApp,
    canShow,
    handoffSession.active,
    isSuppressed,
    router,
    appVersionUpdateModalVisible,
    appRatingModalVisible,
  ]);
}
